import { Link, useParams } from 'react-router-dom'
import { ArrowLeft, ArrowUpRight, BookOpen, FileText, Github } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { projects } from '@/data/projects'
import { useSeo } from '@/hooks/useSeo'
import { trackEvent } from '@/lib/analytics'

const Metric = ({ label, value }: { label: string; value: string }) => (
  <div className="bg-background p-5">
    <p className="text-xs uppercase tracking-widest text-muted-foreground">
      {label}
    </p>
    <p className="mt-1 text-lg font-medium">{value}</p>
  </div>
)

export function ProjectDetailPage() {
  const { slug } = useParams<{ slug: string }>()
  const project = projects.find((p) => p.id === slug)
  useSeo(
    project ? project.title : 'Project Not Found',
    project?.description
  )

  if (!project) {
    return (
      <div className="container flex max-w-3xl flex-col items-center py-32 text-center">
        <h1 className="text-2xl font-semibold tracking-tight">
          Project not found
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          The project you&rsquo;re looking for doesn&rsquo;t exist.
        </p>
        <Button asChild className="mt-6">
          <Link to="/projects">
            <ArrowLeft className="h-4 w-4" />
            Back to projects
          </Link>
        </Button>
      </div>
    )
  }

  return (
    <article className="container max-w-3xl pb-20 pt-12">
      <Button asChild variant="ghost" size="sm" className="-ml-3 mb-6">
        <Link to="/projects">
          <ArrowLeft className="h-3.5 w-3.5" />
          Back to projects
        </Link>
      </Button>

      <header className="border-b border-border/40 pb-8">
        <h1 className="text-3xl font-semibold tracking-tight sm:text-4xl">
          {project.title}
        </h1>
        <p className="mt-3 text-base text-muted-foreground">
          {project.description}
        </p>
        <div className="mt-4 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
          <time className="tabular-nums">
            {new Date(project.date).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
            })}
          </time>
          {project.githubUrl && (
            <>
              <span aria-hidden>&middot;</span>
              <a
                href={project.githubUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 hover:text-foreground"
                onClick={() =>
                  trackEvent('project_link_click', { project_id: project.id, link_type: 'github' })
                }
              >
                <Github className="h-3 w-3" /> Source
              </a>
            </>
          )}
          {project.demoUrl && (
            <>
              <span aria-hidden>&middot;</span>
              <a
                href={project.demoUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 hover:text-foreground"
                onClick={() =>
                  trackEvent('project_link_click', { project_id: project.id, link_type: 'demo' })
                }
              >
                <ArrowUpRight className="h-3 w-3" /> Live demo
              </a>
            </>
          )}
          {project.paperUrl && (
            <>
              <span aria-hidden>&middot;</span>
              <a
                href={project.paperUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 hover:text-foreground"
                onClick={() =>
                  trackEvent('project_link_click', { project_id: project.id, link_type: 'paper' })
                }
              >
                <FileText className="h-3 w-3" /> Paper
              </a>
            </>
          )}
          {project.writeupSlug && (
            <>
              <span aria-hidden>&middot;</span>
              <Link
                to={`/blog/${project.writeupSlug}`}
                className="inline-flex items-center gap-1 hover:text-foreground"
                onClick={() =>
                  trackEvent('project_link_click', { project_id: project.id, link_type: 'writeup' })
                }
              >
                <BookOpen className="h-3 w-3" /> Read the writeup
              </Link>
            </>
          )}
        </div>
      </header>

      <section className="py-8">
        <p className="leading-relaxed text-muted-foreground">
          {project.longDescription}
        </p>
      </section>

      {project.metrics && (
        <section className="grid grid-cols-1 gap-px overflow-hidden rounded-lg border border-border/40 bg-border/40 sm:grid-cols-3">
          {project.metrics.accuracy !== undefined && (
            <Metric label="Accuracy" value={`${project.metrics.accuracy}%`} />
          )}
          {project.metrics.performance && (
            <Metric label="Impact" value={project.metrics.performance} />
          )}
          {project.metrics.dataset && (
            <Metric label="Scale" value={project.metrics.dataset} />
          )}
        </section>
      )}

      <section className="grid grid-cols-1 gap-10 py-10 sm:grid-cols-2">
        <div>
          <h2 className="mb-3 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
            Challenges
          </h2>
          <ul className="space-y-2 text-sm text-muted-foreground">
            {project.challenges.map((c) => (
              <li key={c} className="leading-relaxed">
                {c}
              </li>
            ))}
          </ul>
        </div>
        <div>
          <h2 className="mb-3 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
            Outcomes
          </h2>
          <ul className="space-y-2 text-sm text-muted-foreground">
            {project.outcomes.map((o) => (
              <li key={o} className="leading-relaxed">
                {o}
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="border-t border-border/40 pt-6">
        <h2 className="mb-3 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
          Technologies
        </h2>
        <div className="flex flex-wrap gap-1.5">
          {project.technologies.map((t) => (
            <Badge key={t} variant="secondary" className="font-normal">
              {t}
            </Badge>
          ))}
        </div>
      </section>
    </article>
  )
}
